import { Dialog } from '@angular/cdk/dialog';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CaseSessionsService } from '@features/case-details/case-sessions.service';
import { AnalyticsService, VideoApiService } from '@core/services';
import { PublicationStatus } from '@shared/enums/publication-status.enum';
import { AnalyticsEvents, Video } from '@shared/models';
import { PiiDetectedDialog, PiiDetectionChoice } from './pii-detected-dialog/pii-detected-dialog';
import { filter, from, Observable, switchMap, tap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PublishService {
  private readonly videoApiService = inject(VideoApiService);
  private readonly dialog = inject(Dialog);
  private readonly sessions = inject(CaseSessionsService);
  private readonly router = inject(Router);
  private readonly analytics = inject(AnalyticsService);

  togglePublish(video: Video): void {
    const isPublicationRequest = video.publicationStatus === PublicationStatus.Private;
    if (isPublicationRequest && video.stats.piiCount > 0) {
      this.confirmPublicationDespitePhi(video);
    } else {
      this.changePublicationStatus(video).subscribe({
        next: () => {
          if (isPublicationRequest) {
            this.analytics.trackEvent(AnalyticsEvents.CasePublished, {
              case_id: video.id,
              phi_detected: false,
            });
          } else {
            this.analytics.trackEvent(AnalyticsEvents.CaseUnpublished, { case_id: video.id });
          }
        },
        error: (error) => console.error('Error publishing video:', error),
      });
    }
  }

  private confirmPublicationDespitePhi(video: Video) {
    this.dialog
      .open<PiiDetectionChoice>(PiiDetectedDialog)
      .closed.pipe(
        tap((choice) => {
          if (choice === PiiDetectionChoice.Review) {
            this.reviewFindings(video);
          }
        }),
        filter((choice) => choice === PiiDetectionChoice.Publish),
        switchMap(() => this.changePublicationStatus(video)),
      )
      .subscribe({
        next: () => {
          this.analytics.trackEvent(AnalyticsEvents.CasePublished, {
            case_id: video.id,
            phi_detected: true,
          });
          this.sessions.get(video.id)?.resetPiiList();
        },
        error: (error) => console.error('Error publishing video:', error),
      });
  }

  /** Brings the case on screen, then its findings. */
  private reviewFindings(video: Video): void {
    from(this.router.navigate(['video-details', video.id])).subscribe(() =>
      this.sessions.open(video.id).openPiiPanel(),
    );
  }

  /* The server's answer lands in the session itself: the hub's echo is lost if it beats the group join. */
  private changePublicationStatus(video: Video): Observable<Video> {
    if (!video.id) {
      return throwError(() => new Error('No video id on'));
    }

    const newStatus =
      video.publicationStatus === PublicationStatus.Published
        ? PublicationStatus.Private
        : PublicationStatus.Published;

    return this.videoApiService
      .update(video.id, { publicationStatus: newStatus })
      .pipe(tap((updated) => this.sessions.get(updated.id)?.applyLiveUpdate(updated)));
  }
}
